/** /t/c/<courseId>/cards — print the class's answer cards (STORY-089 M2, FR-036).
 *
 * A card is a black square code with A, B, C and D on its four edges; a student answers by
 * holding the card with their letter at the top, and the teacher's phone reads the room (M3).
 * The screen is a count, a small preview and the print button; the print-only sheet holds every
 * card, one per page. Nothing on a card names the product or a student. */
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect, useId, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { CARD_COUNT, GRID, markerPath } from "../cards/marker";
import { capabilities } from "../platform/capabilities";
import { TeacherChrome } from "../teacher/Chrome";
import { getCourse } from "../teacher/api";
import { Button } from "../ui";

const DEFAULT_COUNT = 30;
const LETTERS = ["A", "B", "C", "D"] as const;

/** Paper geometry in millimetres. An 18 mm module makes a 126 mm marker; with its quiet zone
 * it is 162 mm, and the letter bands bring the card to 175 mm, inside the printable width of
 * A4 and Letter at the 14 mm page margin (`styles.css`). */
const MODULE_MM = 18;
const CARD_MM = 175;
const QUIET_MM = (GRID + 2) * MODULE_MM;
const EDGE_MM = (CARD_MM - QUIET_MM) / 2;
const LETTER_MM = 7; // font size; the capitals stand about 5 mm high
/** Space above each card, so it sits in the middle of an A4 page: (297 - 2 · 14 - 175) / 2.
 * That leaves about 61 mm of paper beyond A and beyond C to hold the card by; on Letter the
 * card still fits, with about 43 mm at C. */
export const PAGE_TOP_MM = 47;

export function Cards() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t, i18n } = useTranslation();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const [count, setCount] = useState(DEFAULT_COUNT);
  const [failed, setFailed] = useState(false);

  useEffect(() => neutralTitle(document, t("cards.documentTitle")), [t, i18n.language]);
  const print = printAction(capabilities, setFailed);

  return (
    <>
      <div className="print:hidden">
        <TeacherChrome title={t("cards.title")} course={course.data ? { id: courseId, title: course.data.title } : null} stage="overview">
          <CardsView count={count} onCount={setCount} print={print} failed={failed} />
        </TeacherChrome>
      </div>
      <CardPrintSheet count={count} />
    </>
  );
}

/** A browser prints the page title in its header; the app's own title is the product name,
 * which has no place on a card. Sets a neutral title and returns the function that puts the old
 * one back, which the route runs when it closes. */
export function neutralTitle(doc: { title: string }, title: string): () => void {
  const before = doc.title;
  doc.title = title;
  return () => {
    doc.title = before;
  };
}

/** The print button's action, or null where there is no button. `window.print()` does nothing
 * in a Capacitor WebView, so in a phone shell (`caps.shell` set) the screen shows the preview and
 * says where to print instead. Elsewhere it prints through `caps.printing`, never
 * `window.print()` (dead in the desktop webview, where the capability asks the shell), and
 * reports whether printing opened. */
export function printAction(
  caps: { shell: unknown; printing: { print: () => Promise<boolean> } },
  onFailed: (failed: boolean) => void,
): (() => Promise<void>) | null {
  if (caps.shell) return null;
  return async () => {
    onFailed(!(await caps.printing.print()));
  };
}

/** The screen's controls, without the route: a test renders it with and without printing. */
export function CardsView({ count, onCount, print, failed = false }: { count: number; onCount: (n: number) => void; print: (() => void | Promise<void>) | null; failed?: boolean }) {
  const { t } = useTranslation();
  const countId = useId();
  const hintId = useId();
  // The field keeps what is typed; the count it drives stays within 1 to 63.
  const [typed, setTyped] = useState(String(count));
  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
      <div className="measure flex flex-col gap-4">
        <p className="text-[color:var(--ink-2)]">{t("cards.lede")}</p>
        <div className="flex flex-col gap-1">
          <label htmlFor={countId} className="font-medium">{t("cards.count")}</label>
          <input
            id={countId}
            className="field num w-28"
            type="number"
            inputMode="numeric"
            name="cards"
            autoComplete="off"
            min={1}
            max={CARD_COUNT}
            value={typed}
            aria-describedby={hintId}
            onChange={(e) => {
              setTyped(e.target.value);
              const n = Math.round(Number(e.target.value));
              if (e.target.value !== "" && Number.isFinite(n)) onCount(Math.min(CARD_COUNT, Math.max(1, n)));
            }}
            onBlur={() => setTyped(String(count))}
          />
          <p id={hintId} className="text-sm text-[color:var(--ink-2)]">{t("cards.countHint", { max: CARD_COUNT })}</p>
        </div>
        <p data-card-pages>{t("cards.pages", { count })}</p>
        {print ? (
          <div className="flex flex-col items-start gap-2">
            <Button data-card-print onClick={() => void print()}>{t("cards.print")}</Button>
            <p className="text-sm text-[color:var(--ink-2)]">{t("cards.printTip")}</p>
            {failed && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{t("cards.printFailed")}</p>}
          </div>
        ) : (
          <p data-card-print-elsewhere className="rounded-[var(--radius-card)] border border-dashed p-3 text-sm">{t("cards.printElsewhere")}</p>
        )}
      </div>
      <figure className="flex flex-col items-center gap-2">
        {/* Paper is white in either theme: this is what the printer will put on it. */}
        <div className="rounded-[var(--radius-card)] border border-[color:var(--line)] bg-white p-2 shadow-sm">
          <CardFace n={1} className="block h-48 w-48" label={t("cards.previewLabel", { n: 1 })} />
        </div>
        <figcaption className="text-sm text-[color:var(--ink-2)]">{t("cards.preview", { n: 1 })}</figcaption>
      </figure>
    </div>
  );
}

/** Every card, one per printed page, and nothing on screen. */
export function CardPrintSheet({ count }: { count: number }) {
  const { t } = useTranslation();
  const n = Math.min(CARD_COUNT, Math.max(1, Math.floor(count)));
  return (
    <div className="hidden print:block" data-card-sheet>
      {Array.from({ length: n }, (_, i) => (
        <section key={i + 1} data-card={i + 1} style={{ breakAfter: i + 1 < n ? "page" : "auto", breakInside: "avoid", paddingTop: `${PAGE_TOP_MM}mm` }}>
          <CardFace n={i + 1} label={t("cards.previewLabel", { n: i + 1 })} style={{ display: "block", width: `${CARD_MM}mm`, height: `${CARD_MM}mm`, margin: "0 auto" }} />
        </section>
      ))}
    </div>
  );
}

/** One card, drawn in millimetres: the marker in its quiet zone, a letter on each edge that
 * reads upright when that edge is at the top, and the number small in each corner. The letters
 * are small so a neighbour cannot easily read a raised answer. Black on white in every theme. */
function CardFace({ n, label, className, style }: { n: number; label: string; className?: string; style?: CSSProperties }) {
  const mid = CARD_MM / 2;
  const band = EDGE_MM / 2;
  const letters: [number, number, number][] = [
    [mid, band, 0],
    [CARD_MM - band, mid, 90],
    [mid, CARD_MM - band, 180],
    [band, mid, 270],
  ];
  const corner = 3.5;
  const corners: [number, number][] = [
    [corner, corner],
    [CARD_MM - corner, corner],
    [CARD_MM - corner, CARD_MM - corner],
    [corner, CARD_MM - corner],
  ];
  const origin = (EDGE_MM + MODULE_MM) / MODULE_MM;
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${CARD_MM} ${CARD_MM}`} className={className} style={style} xmlns="http://www.w3.org/2000/svg">
      <rect width={CARD_MM} height={CARD_MM} fill="#fff" />
      <path d={markerPath(n, origin, origin)} transform={`scale(${MODULE_MM})`} fill="#000" shapeRendering="crispEdges" />
      <g fill="#000" fontFamily="Arial, Helvetica, sans-serif" textAnchor="middle" dominantBaseline="central">
        {letters.map(([x, y, turn], i) => (
          <text key={LETTERS[i]} data-card-letter={LETTERS[i]} x={x} y={y} fontSize={LETTER_MM} fontWeight={700} transform={`rotate(${turn} ${x} ${y})`}>
            {LETTERS[i]}
          </text>
        ))}
        {corners.map(([x, y], i) => (
          <text key={i} data-card-number x={x} y={y} fontSize={3.5}>
            {n}
          </text>
        ))}
      </g>
    </svg>
  );
}
